import { PARTNER_COUNTRIES, partnerCountryName } from "./partnerLetterCountries.js";

/**
 * P167. Every visible string on the public /partner-register flow - the Super
 * Agent's invite letter to a licensed overseas recruitment agency, and the
 * registration form that letter asks them to complete.
 *
 * Two languages, and English is the DEFAULT here - the opposite of the worker
 * and employer pages. Those are read inside Thailand; this letter is addressed
 * to a company abroad, and English is the working language of cross-border
 * recruitment. Thai is offered because the sending Super Agent will want to
 * read their own letter before sending it.
 *
 * Backend-supplied values (the Super Agent's name, appName, the sender's own
 * note text) are NEVER translated here - only this app's own copy is, and
 * callers interpolate the raw backend value into the translated sentence.
 *
 * The letter BODY is a template rather than two hand-written letters per
 * country: only the pieces that genuinely change per country come from the
 * country pack (partnerLetterCountries.js), so adding a country never means
 * re-translating the whole letter.
 */

/**
 * Each language in its own script, which is how a real switcher reads.
 *
 * `bn` and its label are the SAME ones the worker invite page already ships
 * (publicInviteTranslations.js) - one product, one spelling of Bangla.
 *
 * Sri Lanka has two official languages and `si` is only one of them. Sinhala is
 * here because the owner asked for Sri Lanka and it is the majority language;
 * Tamil (`ta`) is a deliberate gap, not an oversight, and adding it is the same
 * shape of work as this.
 */
export const PARTNER_LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "th", label: "ไทย" },
  { code: "bn", label: "বাংলা" },
  { code: "si", label: "සිංහල" },
];

const TRANSLATIONS = {
  en: {
    topbarTag: "Recruitment Partner Registration",

    loadingText: "Opening your invitation letter...",

    envelopeEyebrow: "Global Recruitment Partner Network",
    envelopeFrom: (senderName) => `You have received an invitation from ${senderName}`,
    envelopeHint: "Open the letter to read the full invitation",
    envelopeOpenCta: "Open Letter",
    envelopeFallbackSender: "WorkMate X",

    letterStartCta: "Complete the Partner Registration Form",
    letterNoteLabel: "A note from the sender",

    formBack: "← Back to the letter",
    formHeading: "Partner Registration Form",
    formInviteFrom: (senderName) => `Invitation from ${senderName}`,
    formCompanySection: "Your organization",
    formContactSection: "Authorized contact person",
    formRequiredHint: "Fields marked * are required.",

    fieldCompanyName: "Company / Agency Name",
    fieldCompanyNamePlaceholder: "e.g. Example Overseas Recruiting Ltd.",
    fieldYearEstablished: "Year Established",
    fieldYearEstablishedPlaceholder: "e.g. 2009",
    fieldOfficeLocation: "Office Location",
    fieldOfficeLocationPlaceholder: "City, country",
    fieldWebsite: "Website",
    fieldWebsitePlaceholder: "https://",
    fieldCapacity: "Approximate Worker Supply Capacity",
    fieldCapacityPlaceholder: "e.g. about 500 workers per year",
    fieldCategories: "Main Worker Categories",
    fieldCategoriesPlaceholder: "e.g. Construction, Manufacturing, Agriculture",
    fieldCountriesServed: "Countries Currently Served",
    fieldCountriesServedPlaceholder: "e.g. Malaysia, Saudi Arabia, Singapore",

    fieldContactName: "Authorized Contact Person",
    fieldPosition: "Position",
    fieldPositionPlaceholder: "e.g. Managing Director",
    fieldMobile: "Mobile",
    fieldMobilePlaceholder: "+880...",
    fieldWhatsApp: "WhatsApp",
    fieldEmail: "Email",
    fieldEmailPlaceholder: "name@company.com",

    submitButton: "Submit registration",
    submitButtonSubmitting: "Sending...",
    formFineprint:
      "Submitting this form registers your agency's details for review. It is not a partnership agreement, and it does not grant access to the platform.",

    errorCompanyNameRequired: "Please enter your company / agency name",
    errorLicenseRequired: "Please enter your recruitment licence number",
    errorContactNameRequired: "Please enter the authorized contact person's name",
    errorMobileRequired: "Please enter a mobile number",
    errorEmailRequired: "Please enter an email address",
    errorEmailInvalid: "Please enter a valid email address",
    errorYearInvalid: "Year established must be a real past year",
    submitGenericError: "Unable to submit right now. Please try again.",

    missingInviteTitle: "No invitation link found",
    missingInviteBody:
      "The link you opened has no invite code. Please check the link again, or contact whoever sent it to you.",
    errorTitleNotFound: "This invitation link was not found",
    errorTitleGeneric: "Could not open the invitation link",
    errorBodyGeneric: "Please check the link again, or contact whoever sent it to you.",
    unavailableTitle: "This link is no longer usable",
    unavailableDefault: "Please contact whoever sent you this link for a new one.",

    successTitle: "Your registration has been received",
    successBody: "Your agency's details have been sent to the sender of this invitation.",
    successDuplicateBody: "We already had your agency's details on file from this invitation. There is no need to submit again.",
    successFollowup: (senderName) =>
      `${senderName} will contact your nominated representative to arrange an introductory discussion. This registration is not yet a partnership agreement.`,
    successNextHeading: "What happens next",
    successNextSteps: [
      "Your details are reviewed by the sender's partnership team",
      "Agency verification and KYC",
      "Business discussion, then NDA and MOU if both sides agree",
      "Platform onboarding and a pilot recruitment round",
    ],
    successFallbackName: "The sender",
  },

  th: {
    topbarTag: "ลงทะเบียนพันธมิตรบริษัทจัดหางาน",

    loadingText: "กำลังเปิดจดหมายเชิญ...",

    envelopeEyebrow: "เครือข่ายพันธมิตรจัดหางานระหว่างประเทศ",
    envelopeFrom: (senderName) => `คุณได้รับจดหมายเชิญจาก ${senderName}`,
    envelopeHint: "กดเปิดจดหมายเพื่ออ่านรายละเอียดทั้งหมด",
    envelopeOpenCta: "เปิดจดหมาย",
    envelopeFallbackSender: "WorkMate X",

    letterStartCta: "กรอกแบบฟอร์มลงทะเบียนพันธมิตร",
    letterNoteLabel: "ข้อความจากผู้ส่ง",

    formBack: "← กลับไปอ่านจดหมาย",
    formHeading: "แบบฟอร์มลงทะเบียนพันธมิตร",
    formInviteFrom: (senderName) => `คำเชิญจาก ${senderName}`,
    formCompanySection: "ข้อมูลบริษัทของคุณ",
    formContactSection: "ผู้ติดต่อที่ได้รับมอบอำนาจ",
    formRequiredHint: "ช่องที่มีเครื่องหมาย * จำเป็นต้องกรอก",

    fieldCompanyName: "ชื่อบริษัท / บริษัทจัดหางาน",
    fieldCompanyNamePlaceholder: "เช่น Example Overseas Recruiting Ltd.",
    fieldYearEstablished: "ปีที่ก่อตั้ง",
    fieldYearEstablishedPlaceholder: "เช่น 2009",
    fieldOfficeLocation: "ที่ตั้งสำนักงาน",
    fieldOfficeLocationPlaceholder: "เมือง, ประเทศ",
    fieldWebsite: "เว็บไซต์",
    fieldWebsitePlaceholder: "https://",
    fieldCapacity: "กำลังจัดหาแรงงานโดยประมาณ",
    fieldCapacityPlaceholder: "เช่น ประมาณ 500 คนต่อปี",
    fieldCategories: "ประเภทแรงงานหลัก",
    fieldCategoriesPlaceholder: "เช่น ก่อสร้าง, โรงงาน, เกษตร",
    fieldCountriesServed: "ประเทศที่ให้บริการอยู่ในปัจจุบัน",
    fieldCountriesServedPlaceholder: "เช่น มาเลเซีย, ซาอุดีอาระเบีย, สิงคโปร์",

    fieldContactName: "ชื่อผู้ติดต่อที่ได้รับมอบอำนาจ",
    fieldPosition: "ตำแหน่ง",
    fieldPositionPlaceholder: "เช่น กรรมการผู้จัดการ",
    fieldMobile: "เบอร์มือถือ",
    fieldMobilePlaceholder: "+880...",
    fieldWhatsApp: "WhatsApp",
    fieldEmail: "อีเมล",
    fieldEmailPlaceholder: "name@company.com",

    submitButton: "ส่งข้อมูลลงทะเบียน",
    submitButtonSubmitting: "กำลังส่ง...",
    formFineprint:
      "การส่งแบบฟอร์มนี้เป็นการลงทะเบียนข้อมูลบริษัทเพื่อให้ตรวจสอบเท่านั้น ยังไม่ใช่ข้อตกลงความร่วมมือ และยังไม่ได้รับสิทธิ์เข้าใช้งานระบบ",

    errorCompanyNameRequired: "กรุณากรอกชื่อบริษัท / บริษัทจัดหางาน",
    errorLicenseRequired: "กรุณากรอกเลขที่ใบอนุญาตจัดหางาน",
    errorContactNameRequired: "กรุณากรอกชื่อผู้ติดต่อที่ได้รับมอบอำนาจ",
    errorMobileRequired: "กรุณากรอกเบอร์มือถือ",
    errorEmailRequired: "กรุณากรอกอีเมล",
    errorEmailInvalid: "กรุณากรอกอีเมลให้ถูกต้อง",
    errorYearInvalid: "ปีที่ก่อตั้งต้องเป็นปีในอดีตที่เป็นไปได้จริง",
    submitGenericError: "ไม่สามารถส่งข้อมูลได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง",

    missingInviteTitle: "ไม่พบลิงก์คำเชิญ",
    missingInviteBody: "ลิงก์ที่เปิดไม่มีรหัสคำเชิญ กรุณาตรวจสอบลิงก์อีกครั้ง หรือติดต่อผู้ที่ส่งลิงก์นี้ให้คุณ",
    errorTitleNotFound: "ไม่พบลิงก์คำเชิญนี้",
    errorTitleGeneric: "เปิดลิงก์คำเชิญไม่สำเร็จ",
    errorBodyGeneric: "กรุณาตรวจสอบลิงก์อีกครั้ง หรือติดต่อผู้ที่ส่งลิงก์นี้ให้คุณ",
    unavailableTitle: "ลิงก์นี้ใช้งานไม่ได้แล้ว",
    unavailableDefault: "กรุณาติดต่อผู้ที่ส่งลิงก์นี้ให้คุณเพื่อขอลิงก์ใหม่",

    successTitle: "ได้รับข้อมูลลงทะเบียนของคุณแล้ว",
    successBody: "ข้อมูลบริษัทของคุณถูกส่งให้ผู้ส่งจดหมายเชิญฉบับนี้แล้ว",
    successDuplicateBody: "เราได้รับข้อมูลบริษัทของคุณจากคำเชิญนี้ไว้ก่อนหน้านี้แล้ว ไม่ต้องส่งซ้ำอีกครั้ง",
    successFollowup: (senderName) =>
      `${senderName} จะติดต่อผู้แทนที่คุณระบุไว้เพื่อนัดหมายพูดคุยเบื้องต้น การลงทะเบียนนี้ยังไม่ใช่ข้อตกลงความร่วมมือ`,
    successNextHeading: "ขั้นตอนถัดไป",
    successNextSteps: [
      "ทีมงานของผู้ส่งตรวจสอบข้อมูลของคุณ",
      "ตรวจสอบบริษัทและ KYC",
      "พูดคุยทางธุรกิจ จากนั้นจึงทำ NDA และ MOU หากทั้งสองฝ่ายเห็นชอบ",
      "เริ่มใช้งานระบบ และทดลองจัดหาแรงงานรอบแรก",
    ],
    successFallbackName: "ผู้ส่งจดหมาย",
  },

  bn: {
    topbarTag: "রিক্রুটমেন্ট পার্টনার নিবন্ধন",

    loadingText: "আপনার আমন্ত্রণপত্র খোলা হচ্ছে...",

    envelopeEyebrow: "গ্লোবাল রিক্রুটমেন্ট পার্টনার নেটওয়ার্ক",
    envelopeFrom: (senderName) => `আপনি ${senderName} থেকে একটি আমন্ত্রণ পেয়েছেন`,
    envelopeHint: "সম্পূর্ণ আমন্ত্রণ পড়তে চিঠিটি খুলুন",
    envelopeOpenCta: "চিঠি খুলুন",
    envelopeFallbackSender: "WorkMate X",

    letterStartCta: "পার্টনার নিবন্ধন ফর্ম পূরণ করুন",
    letterNoteLabel: "প্রেরকের বার্তা",

    formBack: "← চিঠিতে ফিরে যান",
    formHeading: "পার্টনার নিবন্ধন ফর্ম",
    formInviteFrom: (senderName) => `${senderName} থেকে আমন্ত্রণ`,
    formCompanySection: "আপনার প্রতিষ্ঠান",
    formContactSection: "অনুমোদিত যোগাযোগকারী ব্যক্তি",
    formRequiredHint: "* চিহ্নিত ঘরগুলো পূরণ করা আবশ্যক।",

    fieldCompanyName: "কোম্পানি / এজেন্সির নাম",
    fieldCompanyNamePlaceholder: "যেমন Example Overseas Recruiting Ltd.",
    fieldYearEstablished: "প্রতিষ্ঠার সাল",
    fieldYearEstablishedPlaceholder: "যেমন 2009",
    fieldOfficeLocation: "অফিসের অবস্থান",
    fieldOfficeLocationPlaceholder: "শহর, দেশ",
    fieldWebsite: "ওয়েবসাইট",
    fieldWebsitePlaceholder: "https://",
    fieldCapacity: "আনুমানিক কর্মী সরবরাহ সক্ষমতা",
    fieldCapacityPlaceholder: "যেমন বছরে প্রায় ৫০০ জন কর্মী",
    fieldCategories: "প্রধান কর্মী শ্রেণি",
    fieldCategoriesPlaceholder: "যেমন নির্মাণ, উৎপাদন, কৃষি",
    fieldCountriesServed: "বর্তমানে যেসব দেশে সেবা দিচ্ছেন",
    fieldCountriesServedPlaceholder: "যেমন মালয়েশিয়া, সৌদি আরব, সিঙ্গাপুর",

    fieldContactName: "অনুমোদিত যোগাযোগকারী ব্যক্তি",
    fieldPosition: "পদবি",
    fieldPositionPlaceholder: "যেমন ব্যবস্থাপনা পরিচালক",
    fieldMobile: "মোবাইল",
    fieldMobilePlaceholder: "+880...",
    fieldWhatsApp: "হোয়াটসঅ্যাপ",
    fieldEmail: "ইমেইল",
    fieldEmailPlaceholder: "name@company.com",

    submitButton: "নিবন্ধন জমা দিন",
    submitButtonSubmitting: "পাঠানো হচ্ছে...",
    formFineprint:
      "এই ফর্ম জমা দিলে আপনার এজেন্সির তথ্য পর্যালোচনার জন্য নিবন্ধিত হয়। এটি কোনো অংশীদারিত্ব চুক্তি নয় এবং এতে প্ল্যাটফর্মে প্রবেশাধিকার দেওয়া হয় না।",

    errorCompanyNameRequired: "অনুগ্রহ করে কোম্পানি / এজেন্সির নাম লিখুন",
    errorLicenseRequired: "অনুগ্রহ করে আপনার রিক্রুটিং লাইসেন্স নম্বর লিখুন",
    errorContactNameRequired: "অনুগ্রহ করে অনুমোদিত যোগাযোগকারী ব্যক্তির নাম লিখুন",
    errorMobileRequired: "অনুগ্রহ করে মোবাইল নম্বর লিখুন",
    errorEmailRequired: "অনুগ্রহ করে ইমেইল ঠিকানা লিখুন",
    errorEmailInvalid: "অনুগ্রহ করে সঠিক ইমেইল ঠিকানা লিখুন",
    errorYearInvalid: "প্রতিষ্ঠার সাল অতীতের একটি বাস্তব সাল হতে হবে",
    submitGenericError: "এই মুহূর্তে জমা দেওয়া যাচ্ছে না। অনুগ্রহ করে আবার চেষ্টা করুন।",

    missingInviteTitle: "কোনো আমন্ত্রণ লিংক পাওয়া যায়নি",
    missingInviteBody:
      "আপনি যে লিংকটি খুলেছেন তাতে কোনো আমন্ত্রণ কোড নেই। অনুগ্রহ করে লিংকটি আবার দেখুন, অথবা যিনি পাঠিয়েছেন তাঁর সঙ্গে যোগাযোগ করুন।",
    errorTitleNotFound: "এই আমন্ত্রণ লিংকটি পাওয়া যায়নি",
    errorTitleGeneric: "আমন্ত্রণ লিংকটি খোলা যায়নি",
    errorBodyGeneric: "অনুগ্রহ করে লিংকটি আবার দেখুন, অথবা যিনি পাঠিয়েছেন তাঁর সঙ্গে যোগাযোগ করুন।",
    unavailableTitle: "এই লিংকটি আর ব্যবহারযোগ্য নয়",
    unavailableDefault: "নতুন লিংকের জন্য যিনি এটি পাঠিয়েছেন তাঁর সঙ্গে যোগাযোগ করুন।",

    successTitle: "আপনার নিবন্ধন গৃহীত হয়েছে",
    successBody: "আপনার এজেন্সির তথ্য এই আমন্ত্রণের প্রেরকের কাছে পাঠানো হয়েছে।",
    successDuplicateBody: "এই আমন্ত্রণ থেকে আপনার এজেন্সির তথ্য আগেই আমাদের কাছে ছিল। আবার জমা দেওয়ার প্রয়োজন নেই।",
    successFollowup: (senderName) =>
      `${senderName} প্রাথমিক আলোচনার জন্য আপনার মনোনীত প্রতিনিধির সঙ্গে যোগাযোগ করবে। এই নিবন্ধন এখনও কোনো অংশীদারিত্ব চুক্তি নয়।`,
    successNextHeading: "এরপর যা হবে",
    successNextSteps: [
      "প্রেরকের পার্টনারশিপ টিম আপনার তথ্য পর্যালোচনা করবে",
      "এজেন্সি যাচাই ও কেওয়াইসি",
      "ব্যবসায়িক আলোচনা, উভয় পক্ষ সম্মত হলে এনডিএ ও এমওইউ",
      "প্ল্যাটফর্মে যুক্ত হওয়া এবং প্রথম পরীক্ষামূলক নিয়োগ",
    ],
    successFallbackName: "প্রেরক",
  },

  si: {
    topbarTag: "බඳවා ගැනීමේ හවුල්කරු ලියාපදිංචිය",

    loadingText: "ඔබගේ ආරාධනා ලිපිය විවෘත වෙමින්...",

    envelopeEyebrow: "ගෝලීය බඳවා ගැනීමේ හවුල්කරු ජාලය",
    envelopeFrom: (senderName) => `ඔබට ${senderName} වෙතින් ආරාධනාවක් ලැබී ඇත`,
    envelopeHint: "සම්පූර්ණ ආරාධනාව කියවීමට ලිපිය විවෘත කරන්න",
    envelopeOpenCta: "ලිපිය විවෘත කරන්න",
    envelopeFallbackSender: "WorkMate X",

    letterStartCta: "හවුල්කරු ලියාපදිංචි ආකෘතිය සම්පූර්ණ කරන්න",
    letterNoteLabel: "යවන්නාගේ පණිවිඩය",

    formBack: "← ලිපියට ආපසු",
    formHeading: "හවුල්කරු ලියාපදිංචි ආකෘතිය",
    formInviteFrom: (senderName) => `${senderName} වෙතින් ආරාධනාව`,
    formCompanySection: "ඔබගේ ආයතනය",
    formContactSection: "බලයලත් සම්බන්ධතා පුද්ගලයා",
    formRequiredHint: "* සලකුණු කර ඇති කොටස් අනිවාර්ය වේ.",

    fieldCompanyName: "සමාගමේ / ඒජන්සියේ නම",
    fieldCompanyNamePlaceholder: "උදා: Example Overseas Recruiting Ltd.",
    fieldYearEstablished: "ආරම්භ කළ වර්ෂය",
    fieldYearEstablishedPlaceholder: "උදා: 2009",
    fieldOfficeLocation: "කාර්යාලයේ පිහිටීම",
    fieldOfficeLocationPlaceholder: "නගරය, රට",
    fieldWebsite: "වෙබ් අඩවිය",
    fieldWebsitePlaceholder: "https://",
    fieldCapacity: "ආසන්න කම්කරු සැපයුම් ධාරිතාව",
    fieldCapacityPlaceholder: "උදා: වසරකට කම්කරුවන් 500ක් පමණ",
    fieldCategories: "ප්‍රධාන කම්කරු ප්‍රවර්ග",
    fieldCategoriesPlaceholder: "උදා: ඉදිකිරීම්, නිෂ්පාදන, කෘෂිකර්මාන්තය",
    fieldCountriesServed: "දැනට සේවය සපයන රටවල්",
    fieldCountriesServedPlaceholder: "උදා: මැලේසියාව, සෞදි අරාබිය, සිංගප්පූරුව",

    fieldContactName: "බලයලත් සම්බන්ධතා පුද්ගලයා",
    fieldPosition: "තනතුර",
    fieldPositionPlaceholder: "උදා: කළමනාකාර අධ්‍යක්ෂ",
    fieldMobile: "ජංගම දුරකථනය",
    fieldMobilePlaceholder: "+94...",
    fieldWhatsApp: "WhatsApp",
    fieldEmail: "විද්‍යුත් තැපෑල",
    fieldEmailPlaceholder: "name@company.com",

    submitButton: "ලියාපදිංචිය යොමු කරන්න",
    submitButtonSubmitting: "යවමින්...",
    formFineprint:
      "මෙම ආකෘතිය යොමු කිරීමෙන් ඔබගේ ඒජන්සියේ තොරතුරු සමාලෝචනය සඳහා ලියාපදිංචි වේ. එය හවුල්කාරිත්ව ගිවිසුමක් නොවන අතර වේදිකාවට ප්‍රවේශය ලබා නොදේ.",

    errorCompanyNameRequired: "කරුණාකර සමාගමේ / ඒජන්සියේ නම ඇතුළත් කරන්න",
    errorLicenseRequired: "කරුණාකර ඔබගේ බඳවා ගැනීමේ බලපත්‍ර අංකය ඇතුළත් කරන්න",
    errorContactNameRequired: "කරුණාකර බලයලත් සම්බන්ධතා පුද්ගලයාගේ නම ඇතුළත් කරන්න",
    errorMobileRequired: "කරුණාකර ජංගම දුරකථන අංකයක් ඇතුළත් කරන්න",
    errorEmailRequired: "කරුණාකර විද්‍යුත් තැපැල් ලිපිනයක් ඇතුළත් කරන්න",
    errorEmailInvalid: "කරුණාකර වලංගු විද්‍යුත් තැපැල් ලිපිනයක් ඇතුළත් කරන්න",
    errorYearInvalid: "ආරම්භ කළ වර්ෂය අතීතයේ තිබූ සැබෑ වර්ෂයක් විය යුතුය",
    submitGenericError: "මේ මොහොතේ යොමු කළ නොහැක. කරුණාකර නැවත උත්සාහ කරන්න.",

    missingInviteTitle: "ආරාධනා සබැඳියක් හමු නොවීය",
    missingInviteBody:
      "ඔබ විවෘත කළ සබැඳියේ ආරාධනා කේතයක් නොමැත. කරුණාකර සබැඳිය නැවත පරීක්ෂා කරන්න, නැතහොත් එය එවූ අය හා සම්බන්ධ වන්න.",
    errorTitleNotFound: "මෙම ආරාධනා සබැඳිය හමු නොවීය",
    errorTitleGeneric: "ආරාධනා සබැඳිය විවෘත කළ නොහැකි විය",
    errorBodyGeneric: "කරුණාකර සබැඳිය නැවත පරීක්ෂා කරන්න, නැතහොත් එය එවූ අය හා සම්බන්ධ වන්න.",
    unavailableTitle: "මෙම සබැඳිය තවදුරටත් භාවිත කළ නොහැක",
    unavailableDefault: "නව සබැඳියක් සඳහා එය ඔබට එවූ අය හා සම්බන්ධ වන්න.",

    successTitle: "ඔබගේ ලියාපදිංචිය ලැබී ඇත",
    successBody: "ඔබගේ ඒජන්සියේ තොරතුරු මෙම ආරාධනාව එවූ අය වෙත යවා ඇත.",
    successDuplicateBody: "මෙම ආරාධනාවෙන් ඔබගේ ඒජන්සියේ තොරතුරු දැනටමත් අප සතුව තිබුණි. නැවත යොමු කිරීම අවශ්‍ය නොවේ.",
    successFollowup: (senderName) =>
      `${senderName} ප්‍රාථමික සාකච්ඡාවක් සඳහා ඔබ නම් කළ නියෝජිතයා හා සම්බන්ධ වනු ඇත. මෙම ලියාපදිංචිය තවම හවුල්කාරිත්ව ගිවිසුමක් නොවේ.`,
    successNextHeading: "ඊළඟට සිදුවන දේ",
    successNextSteps: [
      "යවන්නාගේ හවුල්කාරිත්ව කණ්ඩායම ඔබගේ තොරතුරු සමාලෝචනය කරයි",
      "ඒජන්සිය සත්‍යාපනය සහ KYC",
      "ව්‍යාපාරික සාකච්ඡාව, දෙපාර්ශවයම එකඟ වුවහොත් NDA සහ MOU",
      "වේදිකාවට එක්වීම සහ පළමු පරීක්ෂණාත්මක බඳවා ගැනීම",
    ],
    successFallbackName: "යවන්නා",
  },
};

/**
 * The letter itself, in `language`, for `countryCode`.
 *
 * One template, filled from the country pack - see partnerLetterCountries.js
 * for why. Returns a structure, not HTML: the page decides how a heading, a
 * paragraph, a bullet list and a process flow are drawn, and a letter that
 * carried its own markup could not be restyled without editing prose.
 */
export function buildPartnerLetter(language, countryCode, { appName = "WorkMateX", senderName } = {}) {
  const lang = TRANSLATIONS[language] ? language : "en";
  const country = partnerCountryName(countryCode, lang);
  const pack = LETTER_TEMPLATES[lang];
  return pack(country, countryCode, { appName, senderName });
}

/** Returns the UI dictionary for `language`, falling back to English. */
export function getPublicPartnerText(language) {
  return TRANSLATIONS[language] ?? TRANSLATIONS.en;
}

/**
 * Best-effort mapping from the card's own free-text intendedLanguageCode (set
 * by the sender, never normalized on the backend) to one of the two supported
 * UI language codes - used only to pick the default selected language, never to
 * alter what is displayed about the invitation itself. Returns null (page
 * defaults to English) for an unknown/blank value.
 */
export function inferDefaultPartnerLanguage(card) {
  const lang = (card?.intendedLanguageCode || "").trim().toLowerCase();
  if (lang.startsWith("thai") || lang === "th" || lang.startsWith("ไทย")) {
    return "th";
  }
  if (lang.startsWith("eng") || lang === "en") {
    return "en";
  }
  // Both spellings of Bangla, for the same reason the backoffice formatter
  // accepts both: "bn" predates this screen.
  if (lang === "bn" || lang.startsWith("bang") || lang.startsWith("beng")) {
    return "bn";
  }
  if (lang === "si" || lang.startsWith("sinh")) {
    return "si";
  }
  return null;
}

// --- The letter body, one template per language -----------------------------
//
// Written from the owner's own letter text. Interpolated values are only the
// country name, the app name and the sender's organization name; the substance
// is identical wherever it is sent, which is exactly why it is a template.

const CAPABILITY_KEYS_EN = [
  "Worker registration and structured profiles",
  "Recruitment pipeline management",
  "Identity and document verification",
  "Skills and employment-history information",
  "Worker consent management",
  "AI-assisted job matching",
  "Employer demand management",
  "Recruitment and deployment status tracking",
  "Document workflow",
  "Agency coordination",
  "Role-based access",
  "Data privacy and controlled information sharing",
  "Audit trail and compliance support",
  "Post-deployment workforce coordination",
];

const CAPABILITY_KEYS_TH = [
  "ลงทะเบียนแรงงานและจัดเก็บโปรไฟล์อย่างเป็นระบบ",
  "บริหารสายงานการจัดหาแรงงาน",
  "ตรวจสอบตัวตนและเอกสาร",
  "ข้อมูลทักษะและประวัติการทำงาน",
  "การจัดการความยินยอมของแรงงาน",
  "การจับคู่งานโดยมีระบบช่วยวิเคราะห์",
  "บริหารความต้องการแรงงานของนายจ้าง",
  "ติดตามสถานะการจัดหาและการส่งแรงงาน",
  "ระบบจัดการเอกสาร",
  "การประสานงานระหว่างบริษัทพันธมิตร",
  "การกำหนดสิทธิ์ตามบทบาทหน้าที่",
  "ความเป็นส่วนตัวของข้อมูลและการแบ่งปันข้อมูลอย่างมีขอบเขต",
  "ร่องรอยการตรวจสอบและการปฏิบัติตามกฎระเบียบ",
  "การประสานงานดูแลแรงงานหลังส่งเข้าทำงาน",
];

const SECTOR_KEYS_EN = [
  "Construction",
  "Manufacturing",
  "Industrial",
  "Agriculture",
  "Hospitality",
  "Logistics",
  "Engineering",
  "Skilled Trades",
  "General Workers",
  "Other Qualified Occupations",
];

const CAPABILITY_KEYS_BN = [
  "কর্মী নিবন্ধন ও সুসংগঠিত প্রোফাইল",
  "নিয়োগ পাইপলাইন ব্যবস্থাপনা",
  "পরিচয় ও নথি যাচাই",
  "দক্ষতা ও কর্ম-ইতিহাসের তথ্য",
  "কর্মীর সম্মতি ব্যবস্থাপনা",
  "বিশ্লেষণ-সহায়ক চাকরি ম্যাচিং",
  "নিয়োগকর্তার চাহিদা ব্যবস্থাপনা",
  "নিয়োগ ও প্রেরণের অবস্থা পর্যবেক্ষণ",
  "নথি প্রবাহ ব্যবস্থাপনা",
  "এজেন্সিগুলোর মধ্যে সমন্বয়",
  "ভূমিকাভিত্তিক প্রবেশাধিকার",
  "তথ্যের গোপনীয়তা ও নিয়ন্ত্রিত তথ্য বিনিময়",
  "নিরীক্ষা রেকর্ড ও কমপ্লায়েন্স সহায়তা",
  "প্রেরণ-পরবর্তী কর্মী সমন্বয়",
];

const CAPABILITY_KEYS_SI = [
  "කම්කරු ලියාපදිංචිය සහ ක්‍රමවත් පැතිකඩ",
  "බඳවා ගැනීමේ ක්‍රියාවලිය කළමනාකරණය",
  "අනන්‍යතාව සහ ලේඛන සත්‍යාපනය",
  "කුසලතා සහ රැකියා ඉතිහාස තොරතුරු",
  "කම්කරු කැමැත්ත කළමනාකරණය",
  "විශ්ලේෂණ සහාය ඇති රැකියා ගැළපීම",
  "සේවා යෝජක ඉල්ලුම කළමනාකරණය",
  "බඳවා ගැනීම සහ යැවීමේ තත්ත්වය නිරීක්ෂණය",
  "ලේඛන ප්‍රවාහය",
  "ඒජන්සි අතර සම්බන්ධීකරණය",
  "භූමිකාව අනුව ප්‍රවේශය",
  "දත්ත රහස්‍යතාව සහ පාලිත තොරතුරු බෙදාගැනීම",
  "විගණන වාර්තා සහ අනුකූලතා සහාය",
  "යැවීමෙන් පසු කම්කරු සම්බන්ධීකරණය",
];

const SECTOR_KEYS_BN = [
  "নির্মাণ",
  "উৎপাদন",
  "শিল্প",
  "কৃষি",
  "আতিথেয়তা",
  "লজিস্টিকস",
  "প্রকৌশল",
  "দক্ষ কারিগরি পেশা",
  "সাধারণ কর্মী",
  "অন্যান্য যোগ্য পেশা",
];

const SECTOR_KEYS_SI = [
  "ඉදිකිරීම්",
  "නිෂ්පාදන",
  "කාර්මික",
  "කෘෂිකර්මාන්තය",
  "සත්කාරක සේවා",
  "සැපයුම් සේවා",
  "ඉංජිනේරු",
  "දක්ෂ කර්මාන්ත",
  "සාමාන්‍ය කම්කරුවන්",
  "සුදුසුකම් ඇති අනෙකුත් වෘත්තීන්",
];

const SECTOR_KEYS_TH = [
  "ก่อสร้าง",
  "การผลิต",
  "อุตสาหกรรม",
  "เกษตรกรรม",
  "โรงแรมและบริการ",
  "โลจิสติกส์",
  "วิศวกรรม",
  "ช่างฝีมือ",
  "แรงงานทั่วไป",
  "อาชีพอื่นที่มีคุณสมบัติเหมาะสม",
];

const LETTER_TEMPLATES = {
  en: (country, countryCode, { appName, senderName }) => ({
    subject: `Invitation to Join ${appName} Global Recruitment Partner Network – ${country}`,
    salutation: "Dear Chairman / Managing Director,",
    opening: [
      `Greetings from ${appName}, Thailand.`,
      `We are pleased to invite your organization to explore cooperation with ${appName} – Global Recruitment Partner Network, an integrated technology platform designed to connect legally licensed recruitment agencies, workforce suppliers, employers, and relevant partners through a structured cross-border workforce management ecosystem.`,
    ],
    sections: [
      {
        id: "invitation",
        heading: `Invitation to Licensed Recruitment Agencies in ${country}`,
        paragraphs: [
          LETTER_COUNTRY_LINE("en", countryCode),
          `${appName} is therefore seeking to establish relationships with reputable and legally licensed recruitment agencies in ${country} that have strong recruitment capabilities and are interested in developing workforce opportunities with Thailand and other potential destination markets.`,
          "Our objective is not simply to recruit workers.",
          "Our objective is to build a long-term, transparent and technology-enabled recruitment network where qualified partners can work together efficiently through a coordinated platform.",
        ],
      },
      {
        id: "provides",
        heading: `What ${appName} Provides`,
        paragraphs: [`${appName} is designed to support the recruitment journey through one connected workflow:`],
        flow: [
          "Worker Recruitment",
          "Registration",
          "Verification",
          "Skill Assessment",
          "AI-Assisted Matching",
          "Employer Selection",
          "Documentation",
          "Deployment",
          "Employment Support",
        ],
        afterFlow: [
          `Participating agencies can manage their own recruitment operations and worker supply while coordinating with other authorized parties through the ${appName} platform.`,
          "The platform is designed to support:",
        ],
        bullets: CAPABILITY_KEYS_EN,
      },
      {
        id: "control",
        heading: "Your Business and Network Remain Under Your Control",
        paragraphs: [
          "We understand that worker databases, recruitment networks, business relationships, and employer relationships are important assets of every recruitment agency.",
          `${appName} is designed to facilitate cooperation — not to take over your recruitment business or agent network.`,
          "Access to information can be managed according to the responsibilities and permissions of each participating organization, with appropriate controls for privacy, confidentiality, worker consent, and authorized data sharing.",
        ],
      },
      {
        id: "cooperation",
        heading: `Potential Cooperation with ${country} Partners`,
        paragraphs: ["We are interested in discussing cooperation with agencies capable of supplying workers in areas including:"],
        chips: SECTOR_KEYS_EN,
        afterChips: [
          "Qualified agencies may participate as Recruitment Partners, Supply Partners, Specialized Recruitment Partners, or potential Main Agency / Strategic Partners, subject to verification and mutual agreement.",
        ],
      },
      {
        id: "process",
        heading: "Proposed Partnership Process",
        paragraphs: ["For qualified organizations, the cooperation process may proceed through:"],
        flow: [
          "Initial Registration",
          "Agency Verification",
          "KYC",
          "Business Discussion",
          "NDA",
          "MOU / Cooperation Agreement",
          "Platform Onboarding",
          "Recruitment Integration",
          "Pilot Recruitment",
        ],
        afterFlow: [
          "All recruitment and deployment activities will remain subject to applicable laws, government approvals, licensing requirements, and agreed procedures in the respective countries.",
        ],
      },
      {
        id: "know-you",
        heading: "We Would Like to Know Your Organization",
        paragraphs: [
          `If your company is interested in exploring cooperation with ${appName}, please complete the Partner Registration Form below and nominate an authorized contact person.`,
          "After receiving your information, our International Partnership Coordination Team will contact your nominated representative to arrange an introductory discussion.",
        ],
      },
      {
        id: "corridor",
        heading: LETTER_CORRIDOR_HEADING("en", countryCode),
        paragraphs: LETTER_CORRIDOR_LINES("en", countryCode, appName),
        afterChipsHeading: "Our goal is to make international recruitment:",
        chips: ["More Efficient", "More Transparent", "More Verifiable", "Better Coordinated", "More Responsible"],
        afterChips: [
          `We would be honored to discuss how your organization could become part of the ${appName} Global Recruitment Partner Network.`,
        ],
      },
    ],
    tagline: ["Your Country. Your Recruitment Network. Your Workers.", "Connected Through One Global Platform."],
    closing: "Sincerely,",
    signatureName: senderName || appName,
    signatureUnit: "Global Recruitment Partner Network",
    signatureTagline: "Trusted AI for Responsible Cross-Border Workforce Management",
  }),

  bn: (country, countryCode, { appName, senderName }) => ({
    subject: `${appName} গ্লোবাল রিক্রুটমেন্ট পার্টনার নেটওয়ার্কে যোগদানের আমন্ত্রণ – ${country}`,
    salutation: "সম্মানিত চেয়ারম্যান / ব্যবস্থাপনা পরিচালক,",
    opening: [
      `থাইল্যান্ড থেকে ${appName}-এর শুভেচ্ছা।`,
      `আমরা আপনার প্রতিষ্ঠানকে ${appName} – গ্লোবাল রিক্রুটমেন্ট পার্টনার নেটওয়ার্কের সঙ্গে সহযোগিতার সম্ভাবনা যাচাই করতে আমন্ত্রণ জানাতে পেরে আনন্দিত। এটি একটি সমন্বিত প্রযুক্তি প্ল্যাটফর্ম, যা বৈধ লাইসেন্সপ্রাপ্ত রিক্রুটিং এজেন্সি, কর্মী সরবরাহকারী, নিয়োগকর্তা ও সংশ্লিষ্ট অংশীদারদের একটি সুসংগঠিত আন্তঃসীমান্ত কর্মী ব্যবস্থাপনা কাঠামোর মধ্যে যুক্ত করে।`,
    ],
    sections: [
      {
        id: "invitation",
        heading: `${country}-এর লাইসেন্সপ্রাপ্ত রিক্রুটিং এজেন্সিগুলোর প্রতি আমন্ত্রণ`,
        paragraphs: [
          LETTER_COUNTRY_LINE("bn", countryCode),
          `তাই ${appName} ${country}-এর সুনামসম্পন্ন ও বৈধভাবে লাইসেন্সপ্রাপ্ত সেসব রিক্রুটিং এজেন্সির সঙ্গে সম্পর্ক গড়তে আগ্রহী, যাদের শক্তিশালী নিয়োগ সক্ষমতা রয়েছে এবং যারা থাইল্যান্ড ও অন্যান্য সম্ভাব্য গন্তব্য বাজারে কর্মসংস্থানের সুযোগ গড়ে তুলতে আগ্রহী।`,
          "আমাদের উদ্দেশ্য শুধু কর্মী সংগ্রহ করা নয়।",
          "আমাদের উদ্দেশ্য হলো একটি দীর্ঘমেয়াদি, স্বচ্ছ ও প্রযুক্তিনির্ভর নিয়োগ নেটওয়ার্ক গড়ে তোলা, যেখানে যোগ্য অংশীদাররা একটি সমন্বিত প্ল্যাটফর্মের মাধ্যমে দক্ষতার সঙ্গে একসঙ্গে কাজ করতে পারে।",
        ],
      },
      {
        id: "provides",
        heading: `${appName} যা প্রদান করে`,
        paragraphs: [`${appName} নিয়োগ প্রক্রিয়ার সম্পূর্ণ যাত্রাকে একটি সংযুক্ত কর্মপ্রবাহে সহায়তা করার জন্য তৈরি`],
        flow: [
          "কর্মী সংগ্রহ",
          "নিবন্ধন",
          "যাচাইকরণ",
          "দক্ষতা মূল্যায়ন",
          "বিশ্লেষণ-সহায়ক ম্যাচিং",
          "নিয়োগকর্তার নির্বাচন",
          "নথিপত্র",
          "প্রেরণ",
          "কর্মকালীন সহায়তা",
        ],
        afterFlow: [
          `অংশগ্রহণকারী এজেন্সিগুলো নিজেদের নিয়োগ কার্যক্রম ও কর্মী সরবরাহ নিজেরাই পরিচালনা করতে পারে, একই সঙ্গে ${appName} প্ল্যাটফর্মের মাধ্যমে অনুমোদিত অন্যান্য পক্ষের সঙ্গে সমন্বয় করতে পারে।`,
          "প্ল্যাটফর্মটি নিম্নলিখিত বিষয়গুলো সমর্থন করে",
        ],
        bullets: CAPABILITY_KEYS_BN,
      },
      {
        id: "control",
        heading: "আপনার ব্যবসা ও নেটওয়ার্ক আপনার নিয়ন্ত্রণেই থাকবে",
        paragraphs: [
          "আমরা জানি, কর্মীর তথ্যভাণ্ডার, নিয়োগ নেটওয়ার্ক, ব্যবসায়িক সম্পর্ক ও নিয়োগকর্তার সঙ্গে সম্পর্ক প্রতিটি রিক্রুটিং এজেন্সির গুরুত্বপূর্ণ সম্পদ।",
          `${appName} সহযোগিতা সহজ করার জন্য তৈরি — আপনার নিয়োগ ব্যবসা বা এজেন্ট নেটওয়ার্ক দখল করার জন্য নয়।`,
          "তথ্যে প্রবেশাধিকার প্রতিটি অংশগ্রহণকারী প্রতিষ্ঠানের দায়িত্ব ও অনুমতি অনুযায়ী নির্ধারিত হয়, সঙ্গে থাকে গোপনীয়তা, তথ্য সুরক্ষা, কর্মীর সম্মতি ও অনুমোদিত তথ্য বিনিময়ের যথাযথ নিয়ন্ত্রণ।",
        ],
      },
      {
        id: "cooperation",
        heading: `${country}-এর অংশীদারদের সঙ্গে সম্ভাব্য সহযোগিতা`,
        paragraphs: ["আমরা নিম্নলিখিত ক্ষেত্রগুলোতে কর্মী সরবরাহে সক্ষম এজেন্সিগুলোর সঙ্গে সহযোগিতা নিয়ে আলোচনা করতে আগ্রহী"],
        chips: SECTOR_KEYS_BN,
        afterChips: [
          "যোগ্য এজেন্সিগুলো রিক্রুটমেন্ট পার্টনার, সাপ্লাই পার্টনার, বিশেষায়িত রিক্রুটমেন্ট পার্টনার, অথবা সম্ভাব্য প্রধান এজেন্সি / কৌশলগত অংশীদার হিসেবে যুক্ত হতে পারে, যা যাচাই ও পারস্পরিক সম্মতির ওপর নির্ভরশীল।",
        ],
      },
      {
        id: "process",
        heading: "প্রস্তাবিত অংশীদারিত্ব প্রক্রিয়া",
        paragraphs: ["যোগ্য প্রতিষ্ঠানের ক্ষেত্রে সহযোগিতার প্রক্রিয়া নিম্নলিখিত ধাপে অগ্রসর হতে পারে"],
        flow: [
          "প্রাথমিক নিবন্ধন",
          "এজেন্সি যাচাই",
          "কেওয়াইসি",
          "ব্যবসায়িক আলোচনা",
          "এনডিএ",
          "এমওইউ / সহযোগিতা চুক্তি",
          "প্ল্যাটফর্মে যুক্তকরণ",
          "নিয়োগ প্রক্রিয়ার সংযুক্তি",
          "পরীক্ষামূলক নিয়োগ",
        ],
        afterFlow: [
          "সমস্ত নিয়োগ ও প্রেরণ কার্যক্রম সংশ্লিষ্ট দেশগুলোর প্রযোজ্য আইন, সরকারি অনুমোদন, লাইসেন্সিং শর্ত ও সম্মত পদ্ধতির অধীন থাকবে।",
        ],
      },
      {
        id: "know-you",
        heading: "আমরা আপনার প্রতিষ্ঠান সম্পর্কে জানতে চাই",
        paragraphs: [
          `আপনার প্রতিষ্ঠান যদি ${appName}-এর সঙ্গে সহযোগিতায় আগ্রহী হয়, অনুগ্রহ করে নিচের পার্টনার নিবন্ধন ফর্মটি পূরণ করুন এবং একজন অনুমোদিত যোগাযোগকারী ব্যক্তিকে মনোনীত করুন।`,
          "আপনার তথ্য পাওয়ার পর আমাদের আন্তর্জাতিক পার্টনারশিপ সমন্বয় দল প্রাথমিক আলোচনার জন্য আপনার মনোনীত প্রতিনিধির সঙ্গে যোগাযোগ করবে।",
        ],
      },
      {
        id: "corridor",
        heading: LETTER_CORRIDOR_HEADING("bn", countryCode),
        paragraphs: LETTER_CORRIDOR_LINES("bn", countryCode, appName),
        afterChipsHeading: "আমাদের লক্ষ্য আন্তর্জাতিক নিয়োগকে করা",
        chips: ["আরও দক্ষ", "আরও স্বচ্ছ", "আরও যাচাইযোগ্য", "আরও সুসমন্বিত", "আরও দায়িত্বশীল"],
        afterChips: [
          `আপনার প্রতিষ্ঠান কীভাবে ${appName} গ্লোবাল রিক্রুটমেন্ট পার্টনার নেটওয়ার্কের অংশ হতে পারে, তা নিয়ে আলোচনা করতে পারলে আমরা সম্মানিত বোধ করব।`,
        ],
      },
    ],
    tagline: ["আপনার দেশ। আপনার নিয়োগ নেটওয়ার্ক। আপনার কর্মী।", "একটি বৈশ্বিক প্ল্যাটফর্মের মাধ্যমে সংযুক্ত।"],
    closing: "বিনীত,",
    signatureName: senderName || appName,
    signatureUnit: "গ্লোবাল রিক্রুটমেন্ট পার্টনার নেটওয়ার্ক",
    signatureTagline: "Trusted AI for Responsible Cross-Border Workforce Management",
  }),

  si: (country, countryCode, { appName, senderName }) => ({
    subject: `${appName} ගෝලීය බඳවා ගැනීමේ හවුල්කරු ජාලයට එක්වීමට ආරාධනාව – ${country}`,
    salutation: "ගරු සභාපති / කළමනාකාර අධ්‍යක්ෂ තුමනි,",
    opening: [
      `තායිලන්තයේ ${appName} වෙතින් සුබ පැතුම්.`,
      `නීත්‍යානුකූලව බලපත්‍රලාභී බඳවා ගැනීමේ ඒජන්සි, කම්කරු සපයන්නන්, සේවා යෝජකයන් සහ අදාළ හවුල්කරුවන් ක්‍රමවත් දේශසීමා හරහා කම්කරු කළමනාකරණ පද්ධතියක් තුළ සම්බන්ධ කරන ඒකාබද්ධ තාක්ෂණික වේදිකාවක් වන ${appName} – ගෝලීය බඳවා ගැනීමේ හවුල්කරු ජාලය සමඟ සහයෝගිතාව ගවේෂණය කිරීමට ඔබගේ ආයතනයට ආරාධනා කිරීමට අපි සතුටු වෙමු.`,
    ],
    sections: [
      {
        id: "invitation",
        heading: `${country} හි බලපත්‍රලාභී බඳවා ගැනීමේ ඒජන්සි වෙත ආරාධනාව`,
        paragraphs: [
          LETTER_COUNTRY_LINE("si", countryCode),
          `එබැවින් ${appName}, ${country} හි කීර්තිමත් හා නීත්‍යානුකූලව බලපත්‍රලාභී, ශක්තිමත් බඳවා ගැනීමේ හැකියාවක් ඇති සහ තායිලන්තය හා අනෙකුත් විභව ගමනාන්ත වෙළඳපොළවල් සමඟ කම්කරු අවස්ථා දියුණු කිරීමට උනන්දුවක් දක්වන ඒජන්සි සමඟ සබඳතා ගොඩනගා ගැනීමට බලාපොරොත්තු වේ.`,
          "අපගේ අරමුණ හුදෙක් කම්කරුවන් බඳවා ගැනීම නොවේ.",
          "අපගේ අරමුණ වන්නේ දිගුකාලීන, විනිවිදභාවයෙන් යුත් හා තාක්ෂණයෙන් සවිබල ගැන්වූ බඳවා ගැනීමේ ජාලයක් ගොඩනැගීමයි. එහිදී සුදුසුකම් ලත් හවුල්කරුවන්ට සම්බන්ධීකෘත වේදිකාවක් හරහා කාර්යක්ෂමව එකට වැඩ කළ හැකිය.",
        ],
      },
      {
        id: "provides",
        heading: `${appName} සපයන දේ`,
        paragraphs: [`${appName} නිර්මාණය කර ඇත්තේ බඳවා ගැනීමේ සම්පූර්ණ ගමන එක් සම්බන්ධිත ක්‍රියාවලියක් තුළින් සහාය දැක්වීමටයි`],
        flow: [
          "කම්කරු බඳවා ගැනීම",
          "ලියාපදිංචිය",
          "සත්‍යාපනය",
          "කුසලතා ඇගයීම",
          "විශ්ලේෂණ සහාය ඇති ගැළපීම",
          "සේවා යෝජක තේරීම",
          "ලේඛන කටයුතු",
          "යැවීම",
          "රැකියා කාලය තුළ සහාය",
        ],
        afterFlow: [
          `සහභාගී වන ඒජන්සිවලට තමන්ගේම බඳවා ගැනීමේ කටයුතු සහ කම්කරු සැපයුම කළමනාකරණය කරමින්, ${appName} වේදිකාව හරහා අනෙකුත් බලයලත් පාර්ශව සමඟ සම්බන්ධීකරණය කළ හැකිය.`,
          "වේදිකාව පහත කරුණු සඳහා සහාය දක්වයි",
        ],
        bullets: CAPABILITY_KEYS_SI,
      },
      {
        id: "control",
        heading: "ඔබගේ ව්‍යාපාරය සහ ජාලය ඔබගේ පාලනය යටතේම පවතී",
        paragraphs: [
          "කම්කරු දත්ත ගබඩා, බඳවා ගැනීමේ ජාල, ව්‍යාපාරික සබඳතා සහ සේවා යෝජක සබඳතා සෑම බඳවා ගැනීමේ ඒජන්සියකටම වැදගත් වත්කම් බව අපි තේරුම් ගනිමු.",
          `${appName} නිර්මාණය කර ඇත්තේ සහයෝගිතාව පහසු කිරීමට මිස ඔබගේ බඳවා ගැනීමේ ව්‍යාපාරය හෝ ඒජන්ත ජාලය අත්පත් කර ගැනීමට නොවේ.`,
          "තොරතුරු වෙත ප්‍රවේශය සෑම සහභාගී ආයතනයකගේම වගකීම් හා අවසර අනුව කළමනාකරණය කළ හැකි අතර, පෞද්ගලිකත්වය, රහස්‍යභාවය, කම්කරු කැමැත්ත සහ බලයලත් දත්ත බෙදාගැනීම සඳහා සුදුසු පාලන ක්‍රම ඇත.",
        ],
      },
      {
        id: "cooperation",
        heading: `${country} හවුල්කරුවන් සමඟ විභව සහයෝගිතාව`,
        paragraphs: ["පහත ක්ෂේත්‍රවල කම්කරුවන් සැපයීමට හැකියාව ඇති ඒජන්සි සමඟ සහයෝගිතාව සාකච්ඡා කිරීමට අපි උනන්දු වෙමු"],
        chips: SECTOR_KEYS_SI,
        afterChips: [
          "සුදුසුකම් ලත් ඒජන්සිවලට බඳවා ගැනීමේ හවුල්කරුවන්, සැපයුම් හවුල්කරුවන්, විශේෂිත බඳවා ගැනීමේ හවුල්කරුවන් හෝ විභව ප්‍රධාන ඒජන්සි / උපායමාර්ගික හවුල්කරුවන් ලෙස සහභාගී විය හැකිය. එය සත්‍යාපනය සහ අන්‍යෝන්‍ය එකඟතාවය මත රඳා පවතී.",
        ],
      },
      {
        id: "process",
        heading: "යෝජිත හවුල්කාරිත්ව ක්‍රියාවලිය",
        paragraphs: ["සුදුසුකම් ලත් ආයතන සඳහා සහයෝගිතා ක්‍රියාවලිය පහත පියවර ඔස්සේ ඉදිරියට යා හැකිය"],
        flow: [
          "ප්‍රාථමික ලියාපදිංචිය",
          "ඒජන්සිය සත්‍යාපනය",
          "KYC",
          "ව්‍යාපාරික සාකච්ඡාව",
          "NDA",
          "MOU / සහයෝගිතා ගිවිසුම",
          "වේදිකාවට එක්වීම",
          "බඳවා ගැනීමේ ක්‍රියාවලිය ඒකාබද්ධ කිරීම",
          "පරීක්ෂණාත්මක බඳවා ගැනීම",
        ],
        afterFlow: [
          "සියලුම බඳවා ගැනීමේ සහ යැවීමේ කටයුතු අදාළ රටවල නීති, රාජ්‍ය අනුමැතීන්, බලපත්‍ර අවශ්‍යතා සහ එකඟ වූ ක්‍රියා පටිපාටිවලට යටත් වේ.",
        ],
      },
      {
        id: "know-you",
        heading: "ඔබගේ ආයතනය ගැන දැන ගැනීමට අපි කැමැත්තෙමු",
        paragraphs: [
          `ඔබගේ සමාගම ${appName} සමඟ සහයෝගිතාව ගවේෂණය කිරීමට උනන්දු නම්, කරුණාකර පහත හවුල්කරු ලියාපදිංචි ආකෘතිය සම්පූර්ණ කර බලයලත් සම්බන්ධතා පුද්ගලයෙකු නම් කරන්න.`,
          "ඔබගේ තොරතුරු ලැබීමෙන් පසු, අපගේ ජාත්‍යන්තර හවුල්කාරිත්ව සම්බන්ධීකරණ කණ්ඩායම ප්‍රාථමික සාකච්ඡාවක් සංවිධානය කිරීම සඳහා ඔබ නම් කළ නියෝජිතයා හා සම්බන්ධ වනු ඇත.",
        ],
      },
      {
        id: "corridor",
        heading: LETTER_CORRIDOR_HEADING("si", countryCode),
        paragraphs: LETTER_CORRIDOR_LINES("si", countryCode, appName),
        afterChipsHeading: "ජාත්‍යන්තර බඳවා ගැනීම පහත පරිදි කිරීම අපගේ ඉලක්කයයි",
        chips: ["වඩාත් කාර්යක්ෂම", "වඩාත් විනිවිද පෙනෙන", "වඩාත් සත්‍යාපනය කළ හැකි", "වඩාත් හොඳින් සම්බන්ධීකෘත", "වඩාත් වගකීම් සහගත"],
        afterChips: [
          `ඔබගේ ආයතනය ${appName} ගෝලීය බඳවා ගැනීමේ හවුල්කරු ජාලයේ කොටසක් විය හැකි ආකාරය සාකච්ඡා කිරීමට ලැබීම අපට ගෞරවයකි.`,
        ],
      },
    ],
    tagline: ["ඔබගේ රට. ඔබගේ බඳවා ගැනීමේ ජාලය. ඔබගේ කම්කරුවන්.", "එක් ගෝලීය වේදිකාවක් හරහා සම්බන්ධ වී."],
    closing: "ගෞරවනීයව,",
    signatureName: senderName || appName,
    signatureUnit: "ගෝලීය බඳවා ගැනීමේ හවුල්කරු ජාලය",
    signatureTagline: "Trusted AI for Responsible Cross-Border Workforce Management",
  }),

  th: (country, countryCode, { appName, senderName }) => ({
    subject: `คำเชิญเข้าร่วมเครือข่ายพันธมิตรจัดหางานระหว่างประเทศของ ${appName} – ${country}`,
    salutation: "เรียน ประธานกรรมการ / กรรมการผู้จัดการ",
    opening: [
      `ขอส่งความปรารถนาดีจาก ${appName} ประเทศไทย`,
      `เรามีความยินดีขอเชิญองค์กรของท่านพิจารณาความร่วมมือกับ ${appName} – เครือข่ายพันธมิตรจัดหางานระหว่างประเทศ ซึ่งเป็นแพลตฟอร์มเทคโนโลยีที่เชื่อมโยงบริษัทจัดหางานที่ได้รับอนุญาตอย่างถูกต้องตามกฎหมาย ผู้จัดหาแรงงาน นายจ้าง และพันธมิตรที่เกี่ยวข้อง เข้าไว้ในระบบบริหารจัดการแรงงานข้ามพรมแดนที่เป็นระบบเดียวกัน`,
    ],
    sections: [
      {
        id: "invitation",
        heading: `คำเชิญถึงบริษัทจัดหางานที่ได้รับอนุญาตใน${country}`,
        paragraphs: [
          LETTER_COUNTRY_LINE("th", countryCode),
          `${appName} จึงต้องการสร้างความสัมพันธ์กับบริษัทจัดหางานใน${country}ที่มีชื่อเสียง ได้รับอนุญาตอย่างถูกต้องตามกฎหมาย มีความสามารถในการจัดหาแรงงาน และสนใจพัฒนาโอกาสด้านแรงงานร่วมกับประเทศไทยและตลาดปลายทางอื่น ๆ`,
          "เป้าหมายของเราไม่ใช่เพียงการรับสมัครแรงงาน",
          "เป้าหมายของเราคือการสร้างเครือข่ายจัดหางานระยะยาวที่โปร่งใสและขับเคลื่อนด้วยเทคโนโลยี ซึ่งพันธมิตรที่มีคุณสมบัติเหมาะสมสามารถทำงานร่วมกันได้อย่างมีประสิทธิภาพผ่านแพลตฟอร์มเดียวกัน",
        ],
      },
      {
        id: "provides",
        heading: `สิ่งที่ ${appName} ให้บริการ`,
        paragraphs: [`${appName} ออกแบบมาเพื่อรองรับเส้นทางการจัดหาแรงงานทั้งหมดในกระบวนการเดียวที่เชื่อมต่อกัน`],
        flow: [
          "จัดหาแรงงาน",
          "ลงทะเบียน",
          "ตรวจสอบตัวตน",
          "ประเมินทักษะ",
          "จับคู่งานโดยมีระบบช่วยวิเคราะห์",
          "นายจ้างคัดเลือก",
          "จัดทำเอกสาร",
          "ส่งเข้าทำงาน",
          "ดูแลระหว่างการจ้างงาน",
        ],
        afterFlow: [
          `บริษัทที่เข้าร่วมสามารถบริหารงานจัดหาแรงงานและฐานแรงงานของตนเองได้ พร้อมประสานงานกับผู้ที่ได้รับอนุญาตรายอื่นผ่านแพลตฟอร์ม ${appName}`,
          "แพลตฟอร์มรองรับการทำงานต่อไปนี้",
        ],
        bullets: CAPABILITY_KEYS_TH,
      },
      {
        id: "control",
        heading: "ธุรกิจและเครือข่ายของท่านยังคงอยู่ในการควบคุมของท่าน",
        paragraphs: [
          "เราเข้าใจดีว่าฐานข้อมูลแรงงาน เครือข่ายการจัดหางาน ความสัมพันธ์ทางธุรกิจ และความสัมพันธ์กับนายจ้าง คือสินทรัพย์สำคัญของบริษัทจัดหางานทุกแห่ง",
          `${appName} ออกแบบมาเพื่อสนับสนุนความร่วมมือ — ไม่ใช่เพื่อเข้าไปแทนที่ธุรกิจหรือเครือข่ายตัวแทนของท่าน`,
          "การเข้าถึงข้อมูลกำหนดได้ตามหน้าที่และสิทธิ์ของแต่ละองค์กรที่เข้าร่วม พร้อมมาตรการดูแลความเป็นส่วนตัว การรักษาความลับ ความยินยอมของแรงงาน และการแบ่งปันข้อมูลเฉพาะเท่าที่ได้รับอนุญาต",
        ],
      },
      {
        id: "cooperation",
        heading: `แนวทางความร่วมมือกับพันธมิตรใน${country}`,
        paragraphs: ["เราสนใจหารือความร่วมมือกับบริษัทที่สามารถจัดหาแรงงานในกลุ่มงานต่อไปนี้"],
        chips: SECTOR_KEYS_TH,
        afterChips: [
          "บริษัทที่มีคุณสมบัติเหมาะสมอาจเข้าร่วมในฐานะพันธมิตรจัดหางาน พันธมิตรผู้จัดหาแรงงาน พันธมิตรเฉพาะทาง หรือพันธมิตรหลัก / พันธมิตรเชิงกลยุทธ์ ทั้งนี้ขึ้นอยู่กับการตรวจสอบและข้อตกลงร่วมกัน",
        ],
      },
      {
        id: "process",
        heading: "ขั้นตอนความร่วมมือที่เสนอ",
        paragraphs: ["สำหรับองค์กรที่มีคุณสมบัติเหมาะสม ขั้นตอนความร่วมมืออาจดำเนินไปตามลำดับต่อไปนี้"],
        flow: [
          "ลงทะเบียนเบื้องต้น",
          "ตรวจสอบบริษัท",
          "KYC",
          "หารือทางธุรกิจ",
          "NDA",
          "MOU / ข้อตกลงความร่วมมือ",
          "เริ่มใช้งานระบบ",
          "เชื่อมต่อกระบวนการจัดหาแรงงาน",
          "ทดลองจัดหาแรงงานรอบแรก",
        ],
        afterFlow: [
          "การจัดหาและการส่งแรงงานทั้งหมดยังคงต้องเป็นไปตามกฎหมาย การอนุมัติของภาครัฐ ข้อกำหนดด้านใบอนุญาต และขั้นตอนที่ตกลงกันไว้ของแต่ละประเทศ",
        ],
      },
      {
        id: "know-you",
        heading: "เราอยากรู้จักองค์กรของท่าน",
        paragraphs: [
          `หากบริษัทของท่านสนใจความร่วมมือกับ ${appName} กรุณากรอกแบบฟอร์มลงทะเบียนพันธมิตรด้านล่าง พร้อมระบุผู้ติดต่อที่ได้รับมอบอำนาจ`,
          "เมื่อได้รับข้อมูลแล้ว ทีมประสานงานพันธมิตรระหว่างประเทศของเราจะติดต่อผู้แทนที่ท่านระบุไว้เพื่อนัดหมายพูดคุยเบื้องต้น",
        ],
      },
      {
        id: "corridor",
        heading: LETTER_CORRIDOR_HEADING("th", countryCode),
        paragraphs: LETTER_CORRIDOR_LINES("th", countryCode, appName),
        afterChipsHeading: "เป้าหมายของเราคือทำให้การจัดหาแรงงานระหว่างประเทศ",
        chips: ["มีประสิทธิภาพมากขึ้น", "โปร่งใสมากขึ้น", "ตรวจสอบได้มากขึ้น", "ประสานงานได้ดีขึ้น", "มีความรับผิดชอบมากขึ้น"],
        afterChips: [
          `เราจะรู้สึกเป็นเกียรติอย่างยิ่งที่ได้หารือถึงแนวทางที่องค์กรของท่านจะเข้าร่วมเครือข่ายพันธมิตรจัดหางานระหว่างประเทศของ ${appName}`,
        ],
      },
    ],
    tagline: ["ประเทศของท่าน เครือข่ายของท่าน แรงงานของท่าน", "เชื่อมต่อกันผ่านแพลตฟอร์มเดียว"],
    closing: "ด้วยความนับถือ",
    signatureName: senderName || appName,
    signatureUnit: "เครือข่ายพันธมิตรจัดหางานระหว่างประเทศ",
    signatureTagline: "Trusted AI for Responsible Cross-Border Workforce Management",
  }),
};

// These three read the country pack lazily so a country added there needs no
// change here - and so a missing country degrades to an empty line rather than
// throwing inside a render.
function LETTER_COUNTRY_LINE(language, countryCode) {
  const pack = getCountryPack(countryCode);
  return pack?.sourceCountryLine?.[language] ?? "";
}

function LETTER_CORRIDOR_HEADING(language, countryCode) {
  const pack = getCountryPack(countryCode);
  return pack?.corridorHeading?.[language] ?? "";
}

function LETTER_CORRIDOR_LINES(language, countryCode, appName) {
  const pack = getCountryPack(countryCode);
  const lines = pack?.corridorLines?.[language] ?? [];
  // The country pack writes {appName} rather than the product name - see its
  // own note for why.
  return lines.map((line) => line.replaceAll("{appName}", appName));
}

function getCountryPack(countryCode) {
  return PARTNER_COUNTRIES[String(countryCode ?? "").trim().toUpperCase()] ?? null;
}
